import React from 'react';
import { Link } from 'react-router-dom';
import { titreProDocs } from '../mockPages';
import PageShell, { SectionTitle } from './PageShell';
import { slugFromHref } from '../data/ressourcesFiches';
import { FileText, Download, ExternalLink, BookOpen, Scale, AlertTriangle, BarChart3, Sparkles, ArrowUpRight } from 'lucide-react';

const CAT_ICONS = {
  'Pédagogie & continuum éducatif': BookOpen,
  'Accidentologie · ONISR': BarChart3,
  'Réglementaire & assurance': Scale,
  'Risques & comportements': AlertTriangle,
};

const ELEVE_CATS = [
  'Risques & comportements',
  'Réglementaire & assurance',
  'Accidentologie · ONISR',
  'Pédagogie & continuum éducatif',
];

export default function RessourcesElevePage() {
  const docs = titreProDocs.filter((d) => ELEVE_CATS.includes(d.cat));
  const groups = docs.reduce((acc, d) => { (acc[d.cat] = acc[d.cat] || []).push(d); return acc; }, {});
  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-12">
        <SectionTitle
          kicker="Ressources élève · Fiches chartrées + documents officiels"
          title="La bibliothèque du futur conducteur"
          sub="Pour les sujets clés (alcool, fatigue, énergie cinétique, distance d'arrêt, assurance, points…) tu accèdes à une fiche éditoriale lisible directement en ligne. Le PDF officiel reste téléchargeable en complément."
        />
        <div className="space-y-12">
          {ELEVE_CATS.map((cat) => {
            const list = groups[cat] || [];
            if (!list.length) return null;
            const Icon = CAT_ICONS[cat] || FileText;
            return (
              <div key={cat}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><Icon size={18} /></div>
                  <h3 className="font-serif-editorial text-white text-[24px] leading-tight">{cat}</h3>
                  <span className="text-white/30 font-mono-spec text-[12px]">{list.length} doc{list.length > 1 ? 's' : ''}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                  {list.map((d, i) => {
                    const slug = slugFromHref(d.href);
                    const hasFiche = !!slug;
                    const CardTag = hasFiche ? Link : 'a';
                    const cardProps = hasFiche
                      ? { to: `/ressources/${slug}`, 'data-testid': `ressource-card-${slug}` }
                      : { href: d.href, target: '_blank', rel: 'noopener noreferrer', 'data-testid': `ressource-pdf-${i}` };
                    return (
                      <CardTag key={i} {...cardProps} className="group rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 hover:border-red-700/40 transition flex flex-col card-lift">
                        <div className="flex items-start justify-between mb-3">
                          <FileText className="text-red-400/80 group-hover:text-red-400 transition" size={18} />
                          <div className="flex items-center gap-1.5">
                            {hasFiche && <span className="px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[9.5px] font-bold uppercase tracking-wider">Fiche</span>}
                            <span className="text-[10.5px] uppercase tracking-wider text-white/40 font-semibold font-mono-spec">{d.size}</span>
                          </div>
                        </div>
                        <h4 className="font-serif-editorial text-white text-[17px] leading-tight">{d.name}</h4>
                        <p className="text-white/55 text-[12.5px] mt-2 leading-relaxed flex-1">{d.desc}</p>
                        <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[12px]">
                          <span className="text-red-400 font-semibold inline-flex items-center gap-1.5">
                            {hasFiche ? <><Sparkles size={12} /> Lire la fiche</> : <><Download size={12} /> Télécharger</>}
                          </span>
                          {hasFiche
                            ? <ArrowUpRight size={12} className="text-white/30 group-hover:text-white/70 transition" />
                            : <ExternalLink size={11} className="text-white/30 group-hover:text-white/70 transition" />}
                        </div>
                      </CardTag>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-12 text-white/40 text-[12.5px] max-w-3xl">
          Fiches éditoriales originales codevoiture.fr basées sur des documents officiels gratuits. Sources : Ministère du Travail, Sécurité routière, ONISR.
        </p>
      </section>
    </PageShell>
  );
}
